import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { useInvoiceRequests, useOrderHistory, getOrderItemsSnapshot, markInvoiceAsSent, InvoiceRequestOrder } from '../../lib/invoice-requests-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentRecord } from '../../lib/billing-hooks';
import { cn, formatCurrency } from '../../lib/utils';
import { FileText, Mail, Printer, CheckCircle2, AlertCircle, Loader2, Clock, History, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
type TabKey = 'pending' | 'sent' | 'history';
export function InvoiceRequestsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    pendingRequests,
    sentRequests,
    loading: requestsLoading,
    refetch: refetchRequests
  } = useInvoiceRequests(restaurant?.id);
  const {
    orders: historyOrders,
    loading: historyLoading,
    refetch: refetchHistory
  } = useOrderHistory(restaurant?.id, 200);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurant?.id);
  const [activeTab, setActiveTab] = useState<TabKey>('pending');
  const [processingKey, setProcessingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [invoiceModalOrder, setInvoiceModalOrder] = useState<InvoiceRequestOrder | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalLegalId, setModalLegalId] = useState('');
  const [modalEmail, setModalEmail] = useState('');
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({
      type,
      message
    });
    setTimeout(() => setToast(null), 3500);
  };
  const refetchAll = () => {
    refetchRequests();
    refetchHistory();
  };
  const openSendModal = (order: InvoiceRequestOrder) => {
    setInvoiceModalOrder(order);
    setModalName(order.invoice_customer_name?.trim() || order.customer_name?.trim() || '');
    setModalLegalId(order.invoice_legal_id?.trim() || '');
    setModalEmail(order.invoice_email?.trim() || '');
  };
  const closeSendModal = () => {
    setInvoiceModalOrder(null);
    setModalName('');
    setModalLegalId('');
    setModalEmail('');
  };
  // ─── Asegura que la orden tenga un payment (lo crea si falta) ───────────
  // Reutiliza el payment vinculado a la orden si existe (caso "Enviar más
  // tarde" o factura ya enviada). Si no existe, crea uno nuevo con los
  // items de la orden, igual que el flujo de cobro en caja. Esto permite
  // tanto imprimir como enviar la factura desde el Historial aun cuando
  // la orden no haya pasado por el modal de cobro.
  const ensurePayment = async (order: InvoiceRequestOrder): Promise<PaymentRecord> => {
    if (!restaurant?.id) throw new Error('Restaurante no cargado.');
    const {
      supabase
    } = await import('../../lib/supabase');
    // 1) Si la orden ya tiene un payment vinculado, lo reutilizamos.
    if (order.invoice_payment_id) {
      const {
        data: existing,
        error
      } = await supabase.from('payments').select('*').eq('id', order.invoice_payment_id).single();
      if (!error && existing) return existing as PaymentRecord;
    }
    // 2) Si no, buscamos el último payment registrado para esa orden
    //    (cobros normales en caja generan payments sin marcar la orden).
    const {
      data: anyPayment
    } = await supabase.from('payments').select('*').eq('order_id', order.id).order('created_at', {
      ascending: false
    }).limit(1).maybeSingle();
    if (anyPayment) return anyPayment as PaymentRecord;
    // 3) Si nunca se cobró, generamos uno nuevo (mismo flujo que caja).
    const itemsSnapshot = await getOrderItemsSnapshot(order.id);
    if (itemsSnapshot.length === 0) {
      throw new Error('No se encontraron ítems para esta orden.');
    }
    const result = await processPayment({
      restaurantId: restaurant.id,
      orderId: order.id,
      tableId: null,
      amount: order.total,
      method: 'card',
      items: itemsSnapshot,
      isPartial: false,
      createdBy: tenantCtx?.userId || null
    });
    if (!result.success || !result.payment) {
      throw new Error(result.error || 'Error al crear el registro de pago.');
    }
    return result.payment;
  };
  // ─── Imprimir factura (acción independiente) ────────────────────────────
  // NatuCafé (restaurant_id = 19) → encola un order_print_jobs con
  // target_printer_type='invoice' para que el bridge dual lo envíe
  // DIRECTO a la impresora NOIZA_FACTURA configurada en
  // restaurant_printer_settings. Mismo patrón que usa PayTableModal
  // al cobrar, así el formato del ticket queda 100% consistente.
  //
  // Resto de restaurantes (Cabaña, Rancho, Capricho, etc.) →
  // fallback al flujo anterior (openPrintableInvoice abre una
  // ventana imprimible). Cero impacto en ellos.
  const handlePrint = async (order: InvoiceRequestOrder) => {
    if (!restaurant?.id || !billingInfo) {
      showToast('error', 'Falta información de facturación del restaurante.');
      return;
    }
    const key = `${order.id}:print`;
    setProcessingKey(key);
    try {
      const payment = await ensurePayment(order);
      const isNatuCafe = String(restaurant.id) === '19';
      if (isNatuCafe) {
        const {
          supabase
        } = await import('../../lib/supabase');
        const {
          data: orderRow
        } = await supabase.from('orders').select('tracking_token, order_number, customer_name, customer_phone, delivery_type, table_id, notes, subtotal, delivery_fee, total, source, created_at').eq('id', order.id).single();
        const {
          data: itemRowsRaw
        } = await supabase.from('order_items').select('id, menu_item_id, item_name_snapshot, unit_price_snapshot, quantity, notes, station_code, line_total, status').eq('order_id', order.id);
        const itemRows = (itemRowsRaw || []).filter((r: any) => !['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(r.status || '').toLowerCase().trim()));
        const itemIds = (itemRows || []).map((r: any) => r.id);
        const optionsByItemId: Record<string, any[]> = {};
        if (itemIds.length > 0) {
          const {
            data: optRows
          } = await supabase.from('order_item_options').select('order_item_id, option_group, option_name, price_delta').in('order_item_id', itemIds);
          (optRows || []).forEach((o: any) => {
            if (!optionsByItemId[o.order_item_id]) optionsByItemId[o.order_item_id] = [];
            optionsByItemId[o.order_item_id].push({
              option_group: o.option_group,
              option_name: o.option_name,
              price_delta: o.price_delta
            });
          });
        }
        const itemsPayload = (itemRows || []).map((r: any) => ({
          menu_item_id: r.menu_item_id,
          item_name_snapshot: r.item_name_snapshot,
          unit_price_snapshot: r.unit_price_snapshot,
          quantity: r.quantity,
          notes: r.notes,
          station_code: r.station_code,
          line_total: r.line_total,
          selected_options: optionsByItemId[r.id] || []
        }));
        const invoicePayload = {
          trigger: `invoice_reprint_${payment.id}_${Date.now()}`,
          payment_id: payment.id,
          payment_method: payment.method,
          order_id: order.id,
          order_number: orderRow?.order_number || order.order_number,
          customer_name: orderRow?.customer_name || order.customer_name,
          customer_phone: orderRow?.customer_phone,
          delivery_type: orderRow?.delivery_type || order.delivery_type,
          table_id: orderRow?.table_id,
          notes: orderRow?.notes,
          subtotal: orderRow?.subtotal,
          delivery_fee: orderRow?.delivery_fee,
          total: orderRow?.total ?? order.total,
          source: orderRow?.source,
          created_at: orderRow?.created_at || order.created_at,
          items: itemsPayload
        };
        const {
          error: pjError
        } = await supabase.from('order_print_jobs').insert({
          restaurant_id: 19,
          order_id: orderRow?.tracking_token || null,
          order_number: orderRow?.order_number || order.order_number,
          status: 'pending',
          target_printer_type: 'invoice',
          payload: invoicePayload
        });
        if (pjError) {
          throw new Error(`No se pudo encolar la impresión: ${pjError.message}`);
        }
        showToast('success', '🖨 Enviado a la impresora NOIZA_FACTURA.');
        refetchAll();
        return;
      }
      openPrintableInvoice({
        info: billingInfo,
        payment,
        tableLabel: `Orden #${order.order_number.split('-').pop()}`
      });
      showToast('success', 'Factura lista para imprimir.');
      refetchAll();
    } catch (err: any) {
      console.error('[NOIZA] Error printing invoice:', err);
      showToast('error', err.message || 'Error al imprimir la factura.');
    } finally {
      setProcessingKey(null);
    }
  };
  // ─── Click handler: abre el modal con los datos pre-cargados ───────────
  const handleSendInvoice = (order: InvoiceRequestOrder) => {
    if (!restaurant?.id || !billingInfo) {
      showToast('error', 'Falta información de facturación del restaurante.');
      return;
    }
    openSendModal(order);
  };
  // ─── Submit del modal: ejecuta exactamente el mismo flow que
  //     PayTableModal en "Sí, enviar ahora" → send-invoice-email ───────────
  const doSendInvoice = async (order: InvoiceRequestOrder, name: string, legalId: string, email: string) => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedLegalId = legalId.trim();
    if (!trimmedEmail) {
      showToast('error', 'Falta el correo electrónico del cliente.');
      return;
    }
    const key = `${order.id}:send`;
    setProcessingKey(key);
    try {
      const {
        supabase
      } = await import('../../lib/supabase');
      const payment = await ensurePayment(order);
      // Persistir datos de facturación en la orden (idempotente).
      try {
        await supabase.from('orders').update({
          invoice_customer_name: trimmedName || null,
          invoice_email: trimmedEmail,
          invoice_legal_id: trimmedLegalId || null
        }).eq('id', order.id);
      } catch (e) {
        console.warn('[NOIZA] no se pudo persistir invoice_customer_name/email/legal_id:', e);
      }
      const {
        data: emailRes,
        error: emailErr
      } = await supabase.functions.invoke('send-invoice-email', {
        body: {
          payment_id: payment.id,
          customer_email: trimmedEmail,
          customer_name: trimmedName || null,
          customer_legal_id: trimmedLegalId || null
        }
      });
      if (emailErr) {
        let serverError = emailErr.message || 'Error invocando la función';
        try {
          const ctx: any = (emailErr as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            serverError = body?.error || body?.message || serverError;
          }
        } catch {
          /* swallow */
        }
        throw new Error(`No se pudo enviar el correo: ${serverError}`);
      }
      if (!emailRes?.success) {
        throw new Error(`Respuesta inesperada de la función: ${JSON.stringify(emailRes)}`);
      }
      const markResult = await markInvoiceAsSent(order.id, payment.id);
      if (!markResult.success) {
        console.warn('[NOIZA] markInvoiceAsSent falló pero el correo ya salió:', markResult.error);
      }
      showToast('success', `Factura enviada a ${trimmedEmail}.`);
      closeSendModal();
      refetchAll();
    } catch (err: any) {
      console.error('[NOIZA] Error sending invoice:', err);
      showToast('error', err.message || 'Error inesperado al enviar la factura.');
    } finally {
      setProcessingKey(null);
    }
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4690">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4691">
          <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-4692" />
        </div>
      </AdminLayout>;
  }
  const displayRequests: InvoiceRequestOrder[] = activeTab === 'pending' ? pendingRequests : activeTab === 'sent' ? sentRequests : historyOrders;
  const isListLoading = activeTab === 'history' ? historyLoading : requestsLoading;
  const modalSending = invoiceModalOrder !== null && processingKey === `${invoiceModalOrder.id}:send`;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4693">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-4694">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-4695">
          <div data-id="element-4696">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-4697">
              <FileText className="w-8 h-8 text-amber-500" data-id="element-4698" />
              Facturas
              {pendingRequests.length > 0 && <span className="bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-4699">
                  {pendingRequests.length} pendientes
                </span>}
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-4700">
              Imprimí o enviá por correo la factura de cualquier pedido del
              historial.
            </p>
          </div>
        </div>

        {/* Billing Info Warning */}
        {!infoLoading && !billingInfo && <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 flex gap-3" data-id="element-4701">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-4702" />
            <div data-id="element-4703">
              <h3 className="text-sm font-bold text-red-400" data-id="element-4704">
                Falta información legal
              </h3>
              <p className="text-xs text-red-400/70 mt-1" data-id="element-4705">
                No se han configurado los datos legales de este restaurante. No
                podrás generar facturas hasta que se configuren.
              </p>
            </div>
          </div>}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-px" data-id="element-4706">
          <button onClick={() => setActiveTab('pending')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'pending' ? 'border-amber-500 text-amber-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-4707">
            Pendientes ({pendingRequests.length})
          </button>
          <button onClick={() => setActiveTab('sent')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'sent' ? 'border-emerald-500 text-emerald-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-4708">
            Enviadas hoy ({sentRequests.length})
          </button>
          <button onClick={() => setActiveTab('history')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5', activeTab === 'history' ? 'border-sky-400 text-sky-400' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-4709">
            <History className="w-4 h-4" data-id="element-4710" />
            Historial ({historyOrders.length})
          </button>
        </div>

        {/* Helper text per tab */}
        {activeTab === 'history' && <p className="text-xs text-white/40 mb-4" data-id="element-4711">
            Todos los pedidos no cancelados. Podés imprimir la factura, enviarla
            por correo, o ambas — independientemente de si el cliente la
            solicitó.
          </p>}

        {/* List */}
        {isListLoading ? <div className="flex justify-center py-20" data-id="element-4712">
            <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-4713" />
          </div> : displayRequests.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-4714">
            {activeTab === 'pending' ? <>
                <CheckCircle2 className="w-16 h-16 text-emerald-500/20 mb-4" data-id="element-4715" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-4716">
                  🎉 Sin facturas pendientes
                </h3>
                <p className="text-sm" data-id="element-4717">Todo está al día.</p>
              </> : activeTab === 'sent' ? <>
                <FileText className="w-16 h-16 text-white/10 mb-4" data-id="element-4718" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-4719">
                  No hay facturas enviadas hoy
                </h3>
              </> : <>
                <History className="w-16 h-16 text-white/10 mb-4" data-id="element-4720" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-4721">
                  No hay pedidos en el historial
                </h3>
              </>}
          </div> : <div className="grid gap-4" data-id="element-4722">
            {displayRequests.map(order => {
          const printing = processingKey === `${order.id}:print`;
          const sending = processingKey === `${order.id}:send`;
          const busy = printing || sending;
          const shortOrderNum = order.order_number.split('-').pop();
          const timeAgo = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          const alreadySent = !!order.invoice_sent_at;
          const hasBillingData = !!order.invoice_customer_name || !!order.invoice_email || !!order.invoice_legal_id;
          return <div key={order.id} className="bg-[#141414] border border-white/5 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row gap-4 md:items-center justify-between transition-all hover:border-white/10" data-id="element-4723">
                  <div className="flex-1 min-w-0" data-id="element-4724">
                    <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-4725">
                      <span className="text-lg font-black text-white tabular-nums" data-id="element-4726">
                        #{shortOrderNum}
                      </span>
                      <span className="text-sm font-bold text-[#F4D35E]" data-id="element-4727">
                        {formatCurrency(order.total)}
                      </span>
                      <span className="text-xs font-medium text-white/40 flex items-center gap-1" data-id="element-4728">
                        <Clock className="w-3 h-3" data-id="element-4729" />
                        {timeAgo < 60 ? `Hace ${timeAgo}m` : timeAgo < 60 * 24 ? `Hace ${Math.floor(timeAgo / 60)}h` : `Hace ${Math.floor(timeAgo / (60 * 24))}d`}
                      </span>
                      {alreadySent && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded-md text-[10px] font-bold uppercase tracking-wider" data-id="element-4730">
                          <CheckCircle2 className="w-3 h-3" data-id="element-4731" /> Enviada
                        </span>}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-id="element-4732">
                      <div data-id="element-4733">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-4734">
                          Datos de Facturación
                        </p>
                        {hasBillingData ? <>
                            <p className="font-bold text-white truncate" data-id="element-4735">
                              {order.invoice_customer_name || order.customer_name}
                            </p>
                            {order.invoice_legal_id && <p className="text-white/60 text-xs mt-0.5" data-id="element-4736">
                                ID: {order.invoice_legal_id}
                              </p>}
                            {order.invoice_email && <p className="text-white/60 text-xs mt-0.5 flex items-center gap-1 truncate" data-id="element-4737">
                                <Mail className="w-3 h-3 shrink-0" data-id="element-4738" />{' '}
                                {order.invoice_email}
                              </p>}
                          </> : <p className="text-white/40 text-xs italic" data-id="element-4739">
                            Sin datos — se pedirán al enviar.
                          </p>}
                      </div>

                      <div data-id="element-4740">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-4741">
                          Datos de la Orden
                        </p>
                        <p className="text-white/80 truncate" data-id="element-4742">
                          {order.customer_name}
                        </p>
                        <p className="text-white/60 text-xs mt-0.5 capitalize" data-id="element-4743">
                          {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Local'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col gap-2 shrink-0 border-t border-white/5 pt-4 md:border-t-0 md:pt-0 md:min-w-[200px]" data-id="element-4744">
                    <Button onClick={() => handlePrint(order)} disabled={busy || !billingInfo} variant="outline" className="flex-1 md:flex-none border-white/10 text-white hover:bg-white/5 font-bold" data-id="element-4745">
                      {printing ? <span className="flex items-center justify-center gap-2" data-id="element-4746">
                          <Loader2 className="w-4 h-4 animate-spin" data-id="element-4747" />
                          Imprimiendo...
                        </span> : <span className="flex items-center justify-center gap-2" data-id="element-4748">
                          <Printer className="w-4 h-4" data-id="element-4749" /> Imprimir
                        </span>}
                    </Button>
                    <Button onClick={() => handleSendInvoice(order)} disabled={busy || !billingInfo} className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-black font-bold" data-id="element-4750">
                      {sending ? <span className="flex items-center justify-center gap-2" data-id="element-4751">
                          <Loader2 className="w-4 h-4 animate-spin" data-id="element-4752" />
                          Enviando...
                        </span> : <span className="flex items-center justify-center gap-2" data-id="element-4753">
                          <Mail className="w-4 h-4" data-id="element-4754" />{' '}
                          {alreadySent ? 'Reenviar correo' : 'Enviar correo'}
                        </span>}
                    </Button>
                  </div>
                </div>;
        })}
          </div>}

        {/* Toast */}
        {toast && <div className="fixed bottom-6 right-6 z-50 pointer-events-none" data-id="element-4755">
            <div className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-4756">
              <span className="flex-1" data-id="element-4757">{toast.message}</span>
            </div>
          </div>}

        {/* ─── Modal: Enviar factura por correo ────────────────────────── */}
        {/* Replica el mismo formulario que PayTableModal abre cuando el  */}
        {/* cliente solicita factura. Mismo flujo: nombre / cédula /      */}
        {/* correo → invoke send-invoice-email → markInvoiceAsSent.       */}
        {invoiceModalOrder && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-4758">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" data-id="element-4759">
              {/* Header */}
              <div className="flex items-start justify-between p-5 border-b border-gray-100" data-id="element-4760">
                <div className="flex items-start gap-3" data-id="element-4761">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0" data-id="element-4762">
                    <FileText className="w-5 h-5 text-amber-600" data-id="element-4763" />
                  </div>
                  <div data-id="element-4764">
                    <h2 className="text-lg font-black text-gray-900" data-id="element-4765">
                      Enviar factura por correo
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-4766">
                      Pedido #{invoiceModalOrder.order_number.split('-').pop()}{' '}
                      · {formatCurrency(invoiceModalOrder.total)} · Régimen
                      Simplificado
                    </p>
                  </div>
                </div>
                <button onClick={closeSendModal} disabled={modalSending} className="text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" aria-label="Cerrar" data-id="element-4767">
                  <X className="w-5 h-5" data-id="element-4768" />
                </button>
              </div>

              {/* Form */}
              <div className="p-5 overflow-y-auto space-y-5" data-id="element-4769">
                <div data-id="element-4770">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-4771">
                    A nombre de
                  </label>
                  <input type="text" value={modalName} onChange={e => setModalName(e.target.value)} placeholder="Nombre completo del cliente" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" autoFocus data-id="element-4772" />
                </div>

                <div data-id="element-4773">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-4774">
                    Cédula / Identificación{' '}
                    <span className="text-gray-400 normal-case font-medium" data-id="element-4775">
                      (opcional)
                    </span>
                  </label>
                  <input type="text" value={modalLegalId} onChange={e => setModalLegalId(e.target.value)} placeholder="Ej. 1-1234-5678" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" data-id="element-4776" />
                </div>

                <div data-id="element-4777">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-700 mb-1.5" data-id="element-4778">
                    Correo electrónico
                  </label>
                  <input type="email" value={modalEmail} onChange={e => setModalEmail(e.target.value)} placeholder="cliente@correo.com" disabled={modalSending} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-900 focus:bg-white transition-colors disabled:opacity-50" data-id="element-4779" />
                  <p className="text-xs text-gray-500 mt-1.5" data-id="element-4780">
                    Se enviará la factura electrónica a este correo de forma
                    automática.
                  </p>
                </div>
              </div>

              {/* Footer actions */}
              <div className="p-5 border-t border-gray-100 flex gap-3" data-id="element-4781">
                <Button onClick={closeSendModal} disabled={modalSending} variant="outline" className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50 font-bold" data-id="element-4782">
                  Cancelar
                </Button>
                <Button onClick={() => doSendInvoice(invoiceModalOrder, modalName, modalLegalId, modalEmail)} disabled={modalSending || !modalEmail.trim()} className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-bold" data-id="element-4783">
                  {modalSending ? <span className="flex items-center justify-center gap-2" data-id="element-4784">
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-4785" />
                      Enviando...
                    </span> : <span className="flex items-center justify-center gap-2" data-id="element-4786">
                      <Mail className="w-4 h-4" data-id="element-4787" />
                      Enviar factura
                    </span>}
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}